import type { NodeId } from "./graph";
import type { ControlValue, OrganDef } from "./types";

/**
 * Vokabular aus PRISMA 2 Kap. 14 (Ernährung, S. 138–142) und NaTech 8
 * („Der Weg der Nahrung durch unseren Körper" / „Was geschieht bei der
 * Verdauung?"). „Bauchspeicheldrüse" statt „Pankreas" durchgängig.
 */
const base = import.meta.env.BASE_URL;
const img = (id: string) => `${base}assets/organs/${id}.svg`;

export const ORGANS: OrganDef[] = [
  {
    id: "mund",
    name: "Mund",
    emoji: "👄",
    image: img("mund"),
    controls: [
      { id: "kauen", label: "Kauen", kind: "button", anchor: { x: 50, y: 50 } },
      {
        id: "speichel",
        label: "Speichel",
        kind: "slider",
        min: 0,
        max: 5,
        step: 1,
        anchor: { x: 25, y: 70 },
      },
      { id: "amylase", label: "Amylase-Schub", kind: "button", anchor: { x: 75, y: 70 } },
      { id: "brei", label: "Nahrungsbrei formen", kind: "lever", anchor: { x: 50, y: 85 } },
    ],
  },
  {
    id: "speiseroehre",
    name: "Speiseröhre",
    emoji: "〰️",
    image: img("speiseroehre"),
    controls: [
      { id: "schlucken", label: "Schlucken", kind: "button", anchor: { x: 50, y: 30 } },
      {
        id: "peristaltik",
        label: "Peristaltik-Welle",
        kind: "slider",
        min: 0,
        max: 10,
        step: 1,
        anchor: { x: 50, y: 55 },
      },
      { id: "kehldeckel", label: "Kehldeckel schliessen", kind: "lever", anchor: { x: 50, y: 80 } },
    ],
  },
  {
    id: "magen",
    name: "Magen",
    emoji: "🫃",
    image: img("magen"),
    controls: [
      {
        id: "ph",
        label: "Salzsäure pH",
        kind: "slider",
        min: 1,
        max: 5,
        step: 1,
        unit: "pH",
        anchor: { x: 55, y: 55 },
      },
      { id: "pepsin", label: "Pepsin-Schub", kind: "button", anchor: { x: 80, y: 75 } },
      { id: "durchkneten", label: "Durchkneten", kind: "lever", anchor: { x: 30, y: 80 } },
      {
        id: "pfoertner",
        label: "Pförtner",
        kind: "dial",
        min: 0,
        max: 9,
        step: 1,
        anchor: { x: 90, y: 60 },
      },
    ],
  },
  {
    id: "leber",
    name: "Leber",
    emoji: "🫀",
    image: img("leber"),
    controls: [
      { id: "galle_prod", label: "Galle produzieren", kind: "button", anchor: { x: 48, y: 68 } },
      {
        id: "glykogen",
        label: "Glykogen-Speicher",
        kind: "dial",
        min: 0,
        max: 9,
        step: 1,
        unit: "%",
        anchor: { x: 35, y: 50 },
      },
      { id: "entgiftung", label: "Entgiftung", kind: "lever", anchor: { x: 75, y: 45 } },
      {
        id: "traubenzucker",
        label: "Traubenzucker-Freigabe",
        kind: "slider",
        min: 0,
        max: 10,
        step: 1,
        anchor: { x: 62, y: 88 },
      },
    ],
  },
  {
    id: "gallenblase",
    name: "Gallenblase",
    emoji: "🟢",
    image: img("gallenblase"),
    controls: [
      { id: "galle_store", label: "Galle speichern", kind: "lever", anchor: { x: 50, y: 30 } },
      { id: "galle_release", label: "Galle ausschütten", kind: "button", anchor: { x: 50, y: 70 } },
      {
        id: "fuellstand",
        label: "Füllstand",
        kind: "dial",
        min: 0,
        max: 9,
        step: 1,
        anchor: { x: 30, y: 55 },
      },
    ],
  },
  {
    id: "bauchspeicheldruese",
    name: "Bauchspeicheldrüse",
    emoji: "🫁",
    image: img("bauchspeicheldruese"),
    controls: [
      {
        id: "bauchspeichel",
        label: "Bauchspeichel",
        kind: "slider",
        min: 0,
        max: 10,
        step: 1,
        anchor: { x: 50, y: 53 },
      },
      { id: "amylase", label: "Amylase-Schub", kind: "button", anchor: { x: 25, y: 52 } },
      {
        id: "enzymmix",
        label: "Enzym-Mix",
        kind: "dial",
        min: 0,
        max: 9,
        step: 1,
        anchor: { x: 75, y: 60 },
      },
      { id: "ph_neutral", label: "pH-Neutralisierung", kind: "lever", anchor: { x: 85, y: 75 } },
    ],
  },
  {
    id: "zwoelffingerdarm",
    name: "Zwölffingerdarm",
    emoji: "🔀",
    image: img("zwoelffingerdarm"),
    controls: [
      { id: "galle_einlauf", label: "Galle-Einlauf", kind: "button", anchor: { x: 30, y: 30 } },
      {
        id: "bauchspeichel_einlauf",
        label: "Bauchspeichel-Einlauf",
        kind: "button",
        anchor: { x: 70, y: 30 },
      },
      { id: "mischen", label: "Mischen", kind: "lever", anchor: { x: 50, y: 70 } },
    ],
  },
  {
    id: "duenndarm",
    name: "Dünndarm",
    emoji: "🧬",
    image: img("duenndarm"),
    controls: [
      {
        id: "peristaltik",
        label: "Peristaltik",
        kind: "slider",
        min: 0,
        max: 10,
        step: 1,
        anchor: { x: 45, y: 55 },
      },
      { id: "darmzotten", label: "Darmzotten aufstellen", kind: "lever", anchor: { x: 35, y: 82 } },
      { id: "resorption", label: "Resorption", kind: "button", anchor: { x: 75, y: 65 } },
    ],
  },
  {
    id: "dickdarm",
    name: "Dickdarm",
    emoji: "🧻",
    image: img("dickdarm"),
    controls: [
      {
        id: "wasser",
        label: "Wasser-Rückgewinnung",
        kind: "slider",
        min: 0,
        max: 10,
        step: 1,
        anchor: { x: 50, y: 50 },
      },
      { id: "eindickung", label: "Eindickung", kind: "lever", anchor: { x: 30, y: 75 } },
      {
        id: "bakterien",
        label: "Darmbakterien aktivieren",
        kind: "button",
        anchor: { x: 70, y: 75 },
      },
    ],
  },
  {
    id: "enddarm",
    name: "Enddarm",
    emoji: "💩",
    image: img("enddarm"),
    controls: [
      {
        id: "lagerung",
        label: "Lagerung",
        kind: "dial",
        min: 0,
        max: 9,
        step: 1,
        anchor: { x: 50, y: 40 },
      },
      { id: "schliessmuskel", label: "Schliessmuskel", kind: "lever", anchor: { x: 50, y: 65 } },
      { id: "ausscheiden", label: "Stuhl ausscheiden", kind: "button", anchor: { x: 50, y: 85 } },
    ],
  },
];

export function initialValues(organ: OrganDef): Record<string, ControlValue> {
  const out: Record<string, ControlValue> = {};
  for (const c of organ.controls) {
    if (c.kind === "button" || c.kind === "lever") out[c.id] = false;
    else out[c.id] = c.min ?? 0;
  }
  return out;
}

export function getOrgan(id: NodeId): OrganDef {
  const o = ORGANS.find((o) => o.id === id);
  if (!o) throw new Error(`Unknown organ ${id}`);
  return o;
}
